import { z } from "zod";

import { listQuerySchema } from "../common/list-query";
import { paginatedSchema } from "../common/pagination";

/**
 * The distance pair reads backwards to an operator and deliberately keeps the
 * engine's vocabulary: HIGH always means the reading went above the maximum.
 * Since the sensor measures its own distance from the liquid, DISTANCE_HIGH is
 * an emptying tank and DISTANCE_LOW is an overflow risk. Interface labels say
 * that in words rather than leaving the reader to invert it (ADR-060).
 */
export const INCIDENT_TYPES = [
  "TEMPERATURE_HIGH",
  "TEMPERATURE_LOW",
  "HUMIDITY_HIGH",
  "HUMIDITY_LOW",
  "BATTERY_LOW",
  "DEVICE_OFFLINE",
  "DISTANCE_HIGH",
  "DISTANCE_LOW",
  /**
   * Somebody is in a space at a time nobody should be.
   *
   * No HIGH/LOW pair, because there is no magnitude to be above or below.
   * What decides it is the asset's occupancy expectation and the clock
   * (ADR-069).
   */
  "PRESENCE_UNEXPECTED",
  /**
   * Conducting liquid reached the spot leak probe.
   *
   * No HIGH/LOW pair and no schedule: liquid at the probe is always a fault
   * (ADR-077).
   */
  "LEAK_DETECTED",
  /**
   * Magnetic contact open (door/window) for the rule's duration.
   *
   * No HIGH/LOW pair and no schedule: open contact is always a fault for the
   * duration window (ADR-078).
   */
  "DOOR_OPEN",
  /** Controller channel reading above its configured max (ADR-082). */
  "CHANNEL_HIGH",
  /** Controller channel reading below its configured min (ADR-082). */
  "CHANNEL_LOW",
  /**
   * Controller digital channel in its configured fault state for the duration
   * (ADR-082). No HIGH/LOW pair.
   */
  "CHANNEL_ACTIVE",
  /** RMS current above the circuit's maximum (ADR-102). */
  "CURRENT_HIGH",
  /** RMS current below the circuit's minimum (ADR-102). */
  "CURRENT_LOW",
] as const;

export const incidentTypeSchema = z.enum(INCIDENT_TYPES);
export type IncidentType = z.infer<typeof incidentTypeSchema>;

/**
 * Three states, and only one transition a human performs.
 *
 * ACKNOWLEDGED means somebody has taken it on, not that the condition ended:
 * an acknowledged incident is still unresolved, and the engine keeps updating
 * it. RESOLVED is decided by the readings returning to range, never by a
 * person clicking a button, because a condition does not stop existing
 * because somebody dismissed it.
 */
export const INCIDENT_STATUSES = ["OPEN", "ACKNOWLEDGED", "RESOLVED"] as const;
export const incidentStatusSchema = z.enum(INCIDENT_STATUSES);
export type IncidentStatus = z.infer<typeof incidentStatusSchema>;

export const SEVERITIES = ["INFO", "WARNING", "CRITICAL"] as const;
export const severitySchema = z.enum(SEVERITIES);
export type Severity = z.infer<typeof severitySchema>;

export const ALERT_METRICS = [
  "TEMPERATURE",
  "HUMIDITY",
  "BATTERY",
  "CONNECTIVITY",
  "DISTANCE",
  /**
   * Like CONNECTIVITY, not a number between two bounds: `minValue` and
   * `maxValue` mean nothing for it, and the condition comes from the asset's
   * expected-occupancy hours rather than from the rule (ADR-069).
   */
  "PRESENCE",
  /**
   * Like PRESENCE, not a number between two bounds. Unlike PRESENCE, the
   * condition is the bit itself — leak is always unexpected (ADR-077).
   */
  "LEAK",
  /**
   * Like LEAK: open magnetic contact for the rule's duration is always a
   * fault (ADR-078).
   */
  "DOOR",
  /**
   * Org-wide recipient carrier for numeric controller-channel alerts.
   * Bounds live on DeviceChannel (ADR-082).
   */
  "CHANNEL_RANGE",
  /**
   * Org-wide recipient carrier for digital controller-channel alerts.
   * Fault polarity lives on DeviceChannel (ADR-082).
   */
  "CHANNEL_ACTIVE",
  /**
   * RMS amperes on one conductor. Bounds defer to the asset, the same way
   * temperature does (ADR-102). Not a bit, and not the controller's 4–20 mA.
   */
  "CURRENT",
] as const;
export const alertMetricSchema = z.enum(ALERT_METRICS);
export type AlertMetric = z.infer<typeof alertMetricSchema>;

export const incidentSchema = z.object({
  id: z.uuid(),
  organizationId: z.uuid(),
  organizationName: z.string(),
  deviceId: z.uuid(),
  devEui: z.string(),
  /** Denormalized for display, so a list needs no extra call per row. */
  assetName: z.string().nullable(),
  siteName: z.string().nullable(),
  /** Controller channel that raised the incident, when applicable (ADR-082). */
  channelId: z.uuid().nullable(),
  channelKey: z.string().nullable(),
  channelLabel: z.string().nullable(),

  type: incidentTypeSchema,
  status: incidentStatusSchema,
  severity: severitySchema,

  startedAt: z.iso.datetime(),
  /** Last reading that still met the condition. Shows whether it is ongoing. */
  lastObservedAt: z.iso.datetime(),
  resolvedAt: z.iso.datetime().nullable(),

  acknowledgedAt: z.iso.datetime().nullable(),
  acknowledgedByName: z.string().nullable(),
  acknowledgeNote: z.string().nullable(),

  /** Reading that opened the incident. Null for a device that stopped reporting. */
  triggerValue: z.number().nullable(),
  /** The bound that was crossed, for a row that explains itself. */
  thresholdValue: z.number().nullable(),

  createdAt: z.iso.datetime(),
});

export type Incident = z.infer<typeof incidentSchema>;

export const listIncidentsQuerySchema = listQuerySchema.extend({
  status: incidentStatusSchema.optional(),
  type: incidentTypeSchema.optional(),
  severity: severitySchema.optional(),
  deviceId: z.uuid().optional(),
  /**
   * The default view is what still needs attention. Passing `status` narrows
   * further; `unresolved=false` is how a caller asks for the history.
   */
  unresolved: z.stringbool().default(true),
});

export type ListIncidentsQuery = z.infer<typeof listIncidentsQuerySchema>;

export const acknowledgeIncidentRequestSchema = z.object({
  note: z.string().trim().min(1).max(500).optional(),
});

export type AcknowledgeIncidentRequest = z.infer<typeof acknowledgeIncidentRequestSchema>;

export const incidentListSchema = paginatedSchema(incidentSchema);
export type IncidentList = z.infer<typeof incidentListSchema>;
